
import React, { useState, useEffect } from "react";
import { Sidebar, Menu, SubMenu, MenuItem } from "react-pro-sidebar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosConfig from "../config";
import { Home, LayoutDashboard, Settings, LogOut,  UserCircle, CircleHelp, 
  ChartPie, Hospital, Activity, ChartScatter,
  Shield, UserRoundCog, Key, UserCircle2} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

import _ from "lodash";

export default function MainSidebar() {

  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const userDetails = user?.name ?? { forename: "", surname: "" };

  // gets user's role that has the most permissions (most relevant probably)
  const userRole = (_.maxBy(user?.roles, role => role.permissions))?.name ?? null;

  const handleLogout = async () => {
    try {
      await axiosConfig.delete("/sessions/current");
      await refresh();
      return navigate("/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  useEffect(() => {
    const sidebarContainer = document.querySelector(".ps-sidebar-container");
    if (sidebarContainer) {
      sidebarContainer.style.backgroundColor = "#005EB8";
      sidebarContainer.style.opacity = "1"; 
    }
  }, []);

  return (
    <Sidebar
      collapsed={collapsed}
      onBackdropClick={() => setCollapsed(true)}
      breakPoint="md"
      rootStyles={{
        minWidth: collapsed ? "80px" : "250px",
        width: collapsed ? "80px" : "250px",
        backgroundColor: "#005EB8 !important", // Force the NHS Blue
        borderRight: "1px solid #005EB8 !important", 
        height: "100vh",
        position: "sticky",
        top: 0,
        transition: "all 0.3s ease",
      }}
      style={{
        backgroundColor: "#005EB8 !important", // Force NHS Blue in the final render
        opacity: "1 !important", // Force full opacity
      }}
    >
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "space-between"
      }}>
        {/* User Profile Section */}
        <Menu
          menuItemStyles={{
            button: {
              color: "white",
              fontWeight: "500",
              fontSize: "1rem",
              [`&.active`]: {
                backgroundColor: "#005EB8",
                color: "white",
              },
              "&:hover": {
                backgroundColor: "#003d73",
              },
            },
          }}
        >
          <MenuItem 
            onClick={() => setCollapsed(!collapsed)} 
            icon={<UserCircle size={20} />}
            style={{ 
              marginBottom: "1rem",
              paddingTop: "1rem",
              paddingBottom: "1rem",
            }}
          >
            {!collapsed && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                  {userDetails.forename} {userDetails.surname}
                </span>
                {userRole && (
                  <span style={{ fontSize: "0.75rem", opacity: 1 }}>
                    {userRole}
                  </span>
              )}
              </div>
            )}
          </MenuItem>
        </Menu>
  
      <Menu
        menuItemStyles={{
          button: {
            color: "#ffffff",
            fontWeight: "600",
            fontSize: "1rem",
            [`&.active`]: {
              backgroundColor: "#002244",
              color: "#ffffff",
              borderLeft: "3px solid #ffeb3b" // NHS yellow accent
            },
            "&:hover": {
              backgroundColor: "#002244",
            },
          },
          subMenuContent: {
            backgroundColor: "#003366",
            borderLeft: "3px solid #ffeb3b"
          },
          label: {
            color: "#ffffff !important",
            fontWeight: "600 !important",
          },
          icon: {
            color: "#ffffff !important",
          },
        }}
      >
          <MenuItem
            icon={<Home size={20} />}
            component={<Link to="/home" />}
            className={location.pathname === "/home" ? "active" : ""}
          >
            Home
          </MenuItem>
  
          <MenuItem
            icon={<LayoutDashboard size={20} />}
            component={<Link to="/dashboard" />}
            className={location.pathname === "/dashboard" ? "active" : ""}
          >
            Dashboard
          </MenuItem>

          {/** Behold, the analytics submenu. Expands on the (current) three different types of analytics. */}
          <SubMenu 
            label="Analytics" 
            icon={<Activity size={20} />}
            
          >
            <MenuItem
              icon={<ChartPie size={18} />}
              component={<Link to="/analytics/patient" />}
              className={location.pathname === "/analytics/patient" ? "active" : ""}
            >
              One Patient
            </MenuItem>

            <MenuItem
              icon={<ChartScatter size={18} />}
              component={<Link to="/analytics/all-patients" />}
              className={location.pathname === "/analytics/all-patients" ? "active" : ""}
            >
              All Patients
            </MenuItem>

            <MenuItem
              icon={<Hospital size={18} />}
              component={<Link to="/analytics/hospital" />}
              className={location.pathname === "/analytics/hospital" ? "active" : ""}
            >
              Hospital
            </MenuItem>
          </SubMenu>          
  

          {/** Did someone say admin? When talking to the resident backend expert, here are the admin tables for your viewing pleasure.
           *  I name these children Admin1, Admin2, Admin3. */}

          <SubMenu 
            label="Admin" 
            icon={<Shield size={20} />}
            
          >
            <MenuItem
              icon={<UserRoundCog size={18} />}
              component={<Link to="/admin/user" />}
              className={location.pathname === "/admin/user" ? "active" : ""}
            >
              Users
            </MenuItem>
            <MenuItem
              icon={<Key size={18} />}
              component={<Link to="/admin/authentication" />}
              className={location.pathname === "/admin/authentication" ? "active" : ""}
            >
              Authentication
            </MenuItem>
            <MenuItem
              icon={<UserCircle2 size={18} />}
              component={<Link to="/admin/patient-operations" />}
              className={location.pathname === "/admin/patient-operations" ? "active" : ""}
            >
              Patients
            </MenuItem>
          </SubMenu>
        </Menu>
  
        {/* Bottom Navigation */}
        <Menu
          menuItemStyles={{
            button: {
              color: "white",
              fontWeight: "500",
              fontSize: "1rem",
              [`&.active`]: {
                backgroundColor: "#003d73",
                color: "white",
              },
              "&:hover": {
                backgroundColor: "#003d73",
              },
            },
          }}
        >
          <MenuItem
            icon={<CircleHelp size={20} />}
            component={<Link to="/help" />}
            className={location.pathname === "/help" ? "active" : ""}
          >
            Help
          </MenuItem>
  
          <MenuItem 
            icon={<Settings size={20} />} 
            component={<Link to="/settings" />}
            className={location.pathname === "/settings" ? "active" : ""} 
          >
            Settings
          </MenuItem>
  
          <MenuItem 
            icon={<LogOut size={20} />} 
            onClick={handleLogout}
            style={{ color: "#ff6b6b" }}
          >
            Log Out
          </MenuItem>
        </Menu>
      </div>
    </Sidebar>
  );
}
